import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import debounce from "lodash/debounce";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import flatten from "lodash/flatten";
import intersection from "lodash/intersection";
import omit from "lodash/omit";
import pick from "lodash/pick";
import useRouter from "@stadium/hooks/lib/use-custom-route";
import { useSelector } from "react-redux";
import useAllCountries from "@hooks/countries/use-all-countries";

import { getIsLockerShop, getIsBundleShop } from "@selectors/store";
import { useStoreContext } from "@components/store/provider";
import { getConversionRates } from "@stadium/redux-utils/selectors/configSelector";
import { useConfig } from "@stadium/hooks/lib/use-config";
import { useWindowStorage } from "@hooks/use-window-storage";
import { useCreateSwagContext } from "@contexts/create-swag-context";

import { formatNumberToShortScale } from "@helpers/currency";
import {
  getCurrentPriceRangeSelectedInUsd,
  getPriceRangeInStoreCurrency,
} from "@helpers/products/search";

import {
  CUSTOMIZABLE_FILTER,
  DESIGNED_FILTER,
  LOCKER_INVENTORY_FILTER_KEY,
  PERSISTED_FILTERS_KEY,
  RECOMMENDED_SORT_ITEM,
  ONDEMAND_MODAL_SHOWN_KEY,
  PRODUCT_LISTING_PER_PAGE,
  PRODUCT_LISTING_PER_PAGE_SWAG_CREATION,
  ALL_PRODUCTS_FILTER,
  RELEVANCE_SORT_ITEM,
  ALL_DESIGNED_PRODUCTS_FILTER,
} from "@constants/products/listing";

import { META_QUERY_PARAM_KEY } from "@constants/routes";

export const ProductListingContext = createContext({});

const emptyArr = [];
const emptyObj = {};
const CURRENT_PRICE_DEBOUNCE_DELAY = 1000;
export const INITIAL_PAGE = 1;
export const INIT_PRICE_RANGE_IDS = [0, 0];

const getDefaultSortBy = (brandSlug, searchQuery, menuIdentifier) =>
  brandSlug || searchQuery || menuIdentifier ? RELEVANCE_SORT_ITEM.id : RECOMMENDED_SORT_ITEM.id;

const OldProductListingProvider = ({ children }) => {
  const isLockerShop = useSelector(getIsLockerShop);
  const isBundleShop = useSelector(getIsBundleShop);

  const { isSwagCreationShopFlow } = useCreateSwagContext();

  const router = useRouter();
  const {
    dietaryRestrictionIds = "",
    page = INITIAL_PAGE,
    priceRangeIds = "",
    perPage: routePerPage = isSwagCreationShopFlow ? PRODUCT_LISTING_PER_PAGE_SWAG_CREATION : PRODUCT_LISTING_PER_PAGE,
    colorOptionValueIds = "",
    countryIds = "",
    sizeOptionValueIds = "",
    decorationTypeValues = "",
    custom = "",
    sortBy: querySortBy,
    searchQuery,
    brandValueIds,
    itemType = "",
    productionDays: queryProductionDays = "",
    itemFeatures: queryItemFeatures = "",
    brandSlug,
    designedType,
  } = router.query;

  const {
    storeBasePath,
    persistedFilters,
    setPersistedFilters,
    storeDisplayCurrencyCode,
    category,
    subCategory,
    menuIdentifier,
  } = useStoreContext();

  const sortBy = querySortBy || getDefaultSortBy(brandSlug, searchQuery, menuIdentifier);
  const conversionRates = useConfig(getConversionRates);

  //Getter & Setter for all URL params
  const updatedDietaryRestrictionIds = useMemo(() => {
    return dietaryRestrictionIds ? dietaryRestrictionIds.split(",") : emptyArr;
  }, [dietaryRestrictionIds]);
  const updatedPriceRangeIds = useMemo(() => {
    return priceRangeIds ? [priceRangeIds] : emptyArr;
  }, [priceRangeIds]);
  const updatedBrandValueIds = useMemo(() => {
    return brandValueIds ? brandValueIds.split(",") : emptyArr;
  }, [brandValueIds]);
  const updatedColorOptionValueIds = useMemo(() => {
    return colorOptionValueIds ? colorOptionValueIds.split(",") : emptyArr;
  }, [colorOptionValueIds]);
  const updatedCountryIds = useMemo(() => {
    return countryIds ? countryIds.split(",") : emptyArr;
  }, [countryIds]);
  const updatedSizeOptionValueIds = useMemo(() => {
    return sizeOptionValueIds ? sizeOptionValueIds.split(",") : emptyArr;
  }, [sizeOptionValueIds]);
  const updatedDecorationTypeValues = useMemo(() => {
    return decorationTypeValues ? decorationTypeValues.split(",") : emptyArr;
  }, [decorationTypeValues]);

  useEffect(() => {
    const activeFilters = pick(router.query, PERSISTED_FILTERS_KEY);
    //Persisting filters data in local storage
    if (!isEmpty(activeFilters) && isBundleShop) {
      setPersistedFilters({ ...persistedFilters, ...activeFilters });
    }
  }, [itemType]);

  useEffect(() => {
    if (!isEmpty(updatedPriceRangeIds)) {
      setIsPriceRangeFilterEnabled(true);
      const priceRangeIdInStoreCurrency = getPriceRangeInStoreCurrency(
        priceRangeIds,
        storeDisplayCurrencyCode,
        conversionRates,
      );
      setSelectedPriceRangeIds(priceRangeIdInStoreCurrency);
      setCurrentPriceRangeIds(
        getCurrentPriceRangeSelectedInUsd(
          priceRangeIdInStoreCurrency,
          priceRanges,
          conversionRates,
        ),
      );
    } else {
      setIsPriceRangeFilterEnabled(false);
      setSelectedPriceRangeIds(INIT_PRICE_RANGE_IDS);
    }
  }, [updatedPriceRangeIds, storeDisplayCurrencyCode, conversionRates]);

  const [currentPage, setCurrentPage] = useState(page);
  const [perPage, setPerPage] = useState(Number(routePerPage));
  const [currentCountryIds, setCurrentCountryIds] = useState(updatedCountryIds);
  const [currentDietaryRestrictionsIds, setCurrentDietaryRestrictionIds] = useState(
    updatedDietaryRestrictionIds,
  );
  const [currentPriceRangeIds, setCurrentPriceRangeIds] = useState(updatedPriceRangeIds);
  const [currentColorOptionValueIds, setCurrentColorOptionValueIds] = useState(
    updatedColorOptionValueIds,
  );
  const [currentSizeOptionValueIds, setCurrentSizeOptionValueIds] =
    useState(updatedSizeOptionValueIds);
  const [currentSortBy, setCurrentSortBy] = useState(sortBy);
  const [currentDecorationMethods, setCurrentDecorationMethods] = useState(
    updatedDecorationTypeValues,
  );

  //Filter data comes from the API
  const [priceRanges, setPriceRanges] = useState(emptyObj);
  const [isPriceRangeFilterEnabled, setIsPriceRangeFilterEnabled] = useState(false);
  const [selectedPriceRangeIds, setSelectedPriceRangeIds] = useState(INIT_PRICE_RANGE_IDS);
  const [dietaryRestrictions, setDietaryRestrictions] = useState(emptyArr);
  const [requiredDietaryRestrictionIds, setRequiredDietaryRestrictionIds] = useState();

  const [flattenedBrandValueIds, setFlattenedBrandValueIds] = useState(emptyArr);
  const [currentBrandIds, setCurrentBrandIds] = useState(emptyArr);
  const [brandValues, setBrandValues] = useState(emptyArr);

  const [colorOptionValues, setColorOptionValues] = useState(emptyArr);

  const [sizeOptionValues, setSizeOptionValues] = useState(emptyArr);
  const [routePathForFilters, setRoutePathForFilters] = useState(null);
  const [boxProductList, setBoxProductList] = useState([]);
  const [productionDays, setProductionDays] = useState([]);
  const [itemFeatures, setItemFeatures] = useState(emptyArr);
  const [bulkProductsCount, setBulkProductsCount] = useState(null);
  const [volumeDiscountProductsCount, setVolumeDiscountProductsCount] = useState(null);
  const [onDemandProductsCount, setOnDemandProductsCount] = useState(null);
  const [coreOnDemandProductsCount, setCoreOnDemandProductsCount] = useState(null);
  const [printTypeValues, setPrintTypeValues] = useState(emptyArr);

  const { allCountriesList } = useAllCountries({
    withFlags: true,
    includeGlobalCountry: true,
    showGlobalCountryAtTop: true,
  });

  const countryIdValues = useMemo(
    () =>
      allCountriesList.map((country) => ({
        ...country,
        id: String(country.id),
      })),
    [allCountriesList],
  );

  const handleRoutePathForFilters = (path) => {
    setRoutePathForFilters(path);
  };

  const handleBoxProductList = (list) => {
    setBoxProductList(list);
  };
  const [totalProducts, setTotalProducts] = useState(null);
  const [isFetchingProducts, setIsFetchingProducts] = useState(true);

  useEffect(() => {
    if (isPriceRangeFilterEnabled && selectedPriceRangeIds[1] === INIT_PRICE_RANGE_IDS[1]) {
      setSelectedPriceRangeIds([0, priceRanges.max]);
    }
  }, [isPriceRangeFilterEnabled]);

  useEffect(() => {
    let brandIds = currentBrandIds.map((id) => id.split("-"));
    brandIds = flatten(brandIds).map((id) => +id);
    setFlattenedBrandValueIds(brandIds);
  }, [currentBrandIds]);

  // Filters Selected by the user
  const selectedDietaryLabels = useMemo(() => {
    return dietaryRestrictions.filter((item) => currentDietaryRestrictionsIds.includes(item.id));
  }, [dietaryRestrictions, currentDietaryRestrictionsIds]);

  const selectedBrandLabels = useMemo(() => {
    return brandValues.filter((item) => currentBrandIds.includes(item.id));
  }, [currentBrandIds, brandValues]);

  const selectedPriceRangeLabels = useMemo(() => {
    const [fromPrice, toPrice] = selectedPriceRangeIds;
    if (!isPriceRangeFilterEnabled || !toPrice) return emptyArr;
    return [
      {
        id: "selectedPriceRange",
        name: `Price ${formatNumberToShortScale(fromPrice)}-${formatNumberToShortScale(toPrice)}`,
      },
    ];
  }, [priceRanges, currentPriceRangeIds, selectedPriceRangeIds]);

  const selectedCountryIdTypes = useMemo(() => {
    return countryIdValues.filter((item) => currentCountryIds.includes(item.id));
  }, [countryIdValues, currentCountryIds]);

  const selectedColorOptionTypes = useMemo(() => {
    return colorOptionValues.filter((item) => currentColorOptionValueIds.includes(item.id));
  }, [colorOptionValues, currentColorOptionValueIds]);

  const selectedSizeOptionTypes = useMemo(() => {
    return sizeOptionValues.filter((item) => currentSizeOptionValueIds.includes(item.id));
  }, [sizeOptionValues, currentSizeOptionValueIds]);

  const updateUrl = useCallback(
    (updatedParams, options = emptyObj) => {
      const commonParams = {
        page,
        sortBy,
        perPage,
        ...updatedParams,
      };

      const { pathname } = router;

      const query = {
        ...omit(router.query, META_QUERY_PARAM_KEY),
        countryIds,
        dietaryRestrictionIds,
        priceRangeIds,
        colorOptionValueIds,
        sizeOptionValueIds,
        brandValueIds,
        itemType,
        productionDays: queryProductionDays,
        itemFeatures: queryItemFeatures,
        custom,
        perPage,
        decorationTypeValues,
        designedType,
        ...commonParams,
      };

      router.push(
        { pathname, query },
        {
          scroll: false,
          ...options,
        },
      );
    },
    [
      router,
      category,
      subCategory,
      countryIds,
      dietaryRestrictionIds,
      priceRangeIds,
      brandValueIds,
      colorOptionValueIds,
      sizeOptionValueIds,
      page,
      searchQuery,
      sortBy,
      storeBasePath,
      routePathForFilters,
      custom,
      itemType,
      designedType,
      queryItemFeatures,
      queryProductionDays,
      custom,
      perPage,
    ],
  );

  //URL updaters
  const setPage = useCallback(
    (page) => {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      updateUrl({ page });
    },
    [
      updateUrl,
      countryIds,
      priceRangeIds,
      colorOptionValueIds,
      sizeOptionValueIds,
      decorationTypeValues,
      sortBy,
      dietaryRestrictionIds,
      requiredDietaryRestrictionIds,
      routePathForFilters,
      brandValueIds,
      itemType,
      queryProductionDays,
      queryItemFeatures,
      custom,
      perPage,
    ],
  );

  const updatePerPage = useCallback(
    (perPage) => updateUrl({ perPage, page: INITIAL_PAGE }, { scroll: true }),
    [
      updateUrl,
      countryIds,
      priceRangeIds,
      colorOptionValueIds,
      sizeOptionValueIds,
      decorationTypeValues,
      sortBy,
      dietaryRestrictionIds,
      requiredDietaryRestrictionIds,
      routePathForFilters,
      brandValueIds,
      itemType,
      queryProductionDays,
      queryItemFeatures,
      custom,
      perPage,
    ],
  );

  const setBrandValueIds = useCallback(
    (brandValueIds) => {
      updateUrl({
        brandValueIds: brandValueIds.join(","),
        page: INITIAL_PAGE,
      });
    },
    [
      updateUrl,
      countryIds,
      dietaryRestrictionIds,
      priceRangeIds,
      sizeOptionValueIds,
      decorationTypeValues,
      sortBy,
      category,
      subCategory,
      routePathForFilters,
      colorOptionValueIds,
      storeBasePath,
      itemType,
      queryProductionDays,
      queryItemFeatures,
      custom,
      perPage,
    ],
  );

  const setDietaryRestrictionIds = useCallback(
    (dietaryRestrictionIds) => {
      updateUrl({ dietaryRestrictionIds: dietaryRestrictionIds.join(","), page: INITIAL_PAGE });
    },
    [
      updateUrl,
      countryIds,
      priceRangeIds,
      colorOptionValueIds,
      sizeOptionValueIds,
      decorationTypeValues,
      sortBy,
      requiredDietaryRestrictionIds,
      routePathForFilters,
      brandValueIds,
      storeBasePath,
      itemType,
      queryProductionDays,
      queryItemFeatures,
      custom,
      perPage,
    ],
  );

  const setCountryIds = useCallback(
    (countryIds) => {
      updateUrl({ countryIds: countryIds.join(","), page: INITIAL_PAGE });
    },
    [
      updateUrl,
      countryIds,
      priceRangeIds,
      colorOptionValueIds,
      sizeOptionValueIds,
      decorationTypeValues,
      sortBy,
      requiredDietaryRestrictionIds,
      routePathForFilters,
      brandValueIds,
      storeBasePath,
      itemType,
      queryProductionDays,
      queryItemFeatures,
      custom,
      perPage,
    ],
  );

  const clearPriceFilters = useCallback(() => {
    updateUrl({
      priceRangeIds: emptyArr,
      page: INITIAL_PAGE,
    });
    setIsPriceRangeFilterEnabled(false);
    setSelectedPriceRangeIds(INIT_PRICE_RANGE_IDS);
  }, [
    updateUrl,
    countryIds,
    priceRanges,
    conversionRates,
    dietaryRestrictionIds,
    decorationTypeValues,
    colorOptionValueIds,
    sizeOptionValueIds,
    brandValueIds,
    sortBy,
    category,
    subCategory,
    storeBasePath,
    itemType,
    queryProductionDays,
    queryItemFeatures,
    custom,
    perPage,
  ]);

  const debouncedSetUrlPriceRangeIds = useCallback(
    debounce((priceRangesIds) => {
      updateUrl({
        priceRangeIds: [
          getCurrentPriceRangeSelectedInUsd(priceRangesIds, priceRanges, conversionRates),
        ],
        page: INITIAL_PAGE,
      });
    }, CURRENT_PRICE_DEBOUNCE_DELAY),
    [
      priceRanges,
      conversionRates,
      updateUrl,
      countryIds,
      dietaryRestrictionIds,
      colorOptionValueIds,
      sizeOptionValueIds,
      decorationTypeValues,
      brandValueIds,
      sortBy,
      category,
      subCategory,
      routePathForFilters,
      storeBasePath,
      itemType,
      queryProductionDays,
      queryItemFeatures,
      custom,
      perPage,
    ],
  );

  const setPriceRangeIds = (priceRangeIds) => {
    if (isEmpty(priceRangeIds)) {
      clearPriceFilters();
      return;
    }
    if (priceRangeIds[0] === priceRangeIds[1]) return;
    setSelectedPriceRangeIds(priceRangeIds);
    debouncedSetUrlPriceRangeIds(priceRangeIds);
  };

  const setColorOptionValueIds = useCallback(
    (colorOptionValueIds) => {
      updateUrl({
        colorOptionValueIds: colorOptionValueIds.join(","),
        page: INITIAL_PAGE,
      });
    },
    [
      updateUrl,
      countryIds,
      dietaryRestrictionIds,
      priceRangeIds,
      sizeOptionValueIds,
      decorationTypeValues,
      sortBy,
      category,
      subCategory,
      routePathForFilters,
      brandValueIds,
      storeBasePath,
      itemType,
      queryProductionDays,
      queryItemFeatures,
      custom,
      perPage,
    ],
  );

  const setSizeOptionValueIds = useCallback(
    (sizeOptionValueIds) => {
      updateUrl({ sizeOptionValueIds: sizeOptionValueIds.join(","), page: INITIAL_PAGE });
    },
    [
      updateUrl,
      countryIds,
      dietaryRestrictionIds,
      priceRangeIds,
      colorOptionValueIds,
      decorationTypeValues,
      sortBy,
      category,
      subCategory,
      routePathForFilters,
      brandValueIds,
      storeBasePath,
      itemType,
      queryProductionDays,
      queryItemFeatures,
      custom,
      perPage,
    ],
  );

  const setSortBy = useCallback(
    (sortBy) => updateUrl({ sortBy, page: INITIAL_PAGE }),
    [
      updateUrl,
      countryIds,
      dietaryRestrictionIds,
      priceRangeIds,
      colorOptionValueIds,
      sizeOptionValueIds,
      decorationTypeValues,
      category,
      subCategory,
      routePathForFilters,
      brandValueIds,
      storeBasePath,
      itemType,
      queryProductionDays,
      queryItemFeatures,
      custom,
      perPage,
    ],
  );

  const setCustomizableFilter = useCallback(
    (custom) => updateUrl({ custom, page: INITIAL_PAGE }),
    [
      updateUrl,
      countryIds,
      dietaryRestrictionIds,
      priceRangeIds,
      colorOptionValueIds,
      sizeOptionValueIds,
      decorationTypeValues,
      category,
      subCategory,
      routePathForFilters,
      brandValueIds,
      storeBasePath,
      itemType,
      queryProductionDays,
      queryItemFeatures,
      custom,
      perPage,
    ],
  );

  const setSelectedItemTypes = useCallback(
    (itemType) => updateUrl({ itemType: itemType?.join() || "", page: INITIAL_PAGE }),
    [
      updateUrl,
      countryIds,
      dietaryRestrictionIds,
      priceRangeIds,
      colorOptionValueIds,
      sizeOptionValueIds,
      decorationTypeValues,
      category,
      subCategory,
      routePathForFilters,
      brandValueIds,
      storeBasePath,
      itemType,
      queryProductionDays,
      queryItemFeatures,
      custom,
      perPage,
    ],
  );

  const setSelectedDesignedTypes = useCallback(
    (designedType) => updateUrl({ designedType: designedType?.join() || "", page: INITIAL_PAGE }),
    [updateUrl],
  );

  const setProductionDaysFilter = useCallback(
    (productionDays) =>
      updateUrl({ productionDays: productionDays?.join() || [], page: INITIAL_PAGE }),
    [
      updateUrl,
      countryIds,
      dietaryRestrictionIds,
      priceRangeIds,
      colorOptionValueIds,
      sizeOptionValueIds,
      decorationTypeValues,
      category,
      subCategory,
      routePathForFilters,
      brandValueIds,
      storeBasePath,
      itemType,
      queryProductionDays,
      queryItemFeatures,
      custom,
      perPage,
    ],
  );

  const setDecorationMethodsFilter = useCallback(
    (decorationMethods) =>
      updateUrl({ decorationTypeValues: decorationMethods?.join(",") || [], page: INITIAL_PAGE }),
    [
      updateUrl,
      countryIds,
      dietaryRestrictionIds,
      priceRangeIds,
      colorOptionValueIds,
      sizeOptionValueIds,
      decorationTypeValues,
      category,
      subCategory,
      routePathForFilters,
      brandValueIds,
      storeBasePath,
      itemType,
      queryProductionDays,
      queryItemFeatures,
      custom,
      perPage,
    ],
  );

  const setItemFeaturesFilter = useCallback(
    (itemFeatures) => updateUrl({ itemFeatures: itemFeatures?.join() || [], page: INITIAL_PAGE }),
    [
      updateUrl,
      countryIds,
      dietaryRestrictionIds,
      priceRangeIds,
      colorOptionValueIds,
      sizeOptionValueIds,
      decorationTypeValues,
      category,
      subCategory,
      routePathForFilters,
      brandValueIds,
      storeBasePath,
      itemType,
      queryProductionDays,
      queryItemFeatures,
      custom,
      perPage,
    ],
  );

  // to update the page state from the params
  useEffect(() => {
    if (page !== currentPage) setCurrentPage(page);
  }, [page, currentPage, setCurrentPage]);

  // to update the page state from the params
  useEffect(() => {
    if (routePerPage !== perPage) setPerPage(Number(routePerPage));
  }, [perPage, routePerPage, setPerPage]);

  // to update the dietaryRestrictionIds state from the params
  useEffect(() => {
    if (!isEqual(updatedDietaryRestrictionIds, currentDietaryRestrictionsIds)) {
      setCurrentDietaryRestrictionIds(updatedDietaryRestrictionIds);
    }
  }, [
    updatedDietaryRestrictionIds,
    currentDietaryRestrictionsIds,
    setCurrentDietaryRestrictionIds,
  ]);

  // to update the priceRangeIds state from the params
  useEffect(() => {
    if (!isEqual(updatedPriceRangeIds, currentPriceRangeIds))
      setCurrentPriceRangeIds(updatedPriceRangeIds);
  }, [updatedPriceRangeIds, currentPriceRangeIds, setCurrentPriceRangeIds]);

  // to update the brandValueIds state from the params

  useEffect(() => {
    if (!isEqual(updatedCountryIds, currentCountryIds)) setCurrentCountryIds(updatedCountryIds);
  }, [updatedCountryIds, currentCountryIds, setCurrentCountryIds]);

  useEffect(() => {
    if (!isEqual(updatedBrandValueIds, currentBrandIds)) setCurrentBrandIds(updatedBrandValueIds);
  }, [updatedBrandValueIds, currentBrandIds, setCurrentBrandIds]);

  //to update the colorOptionValueIds state from params
  useEffect(() => {
    if (!isEqual(updatedColorOptionValueIds, currentColorOptionValueIds))
      setCurrentColorOptionValueIds(updatedColorOptionValueIds);
  }, [updatedColorOptionValueIds, currentColorOptionValueIds, setCurrentColorOptionValueIds]);

  //to update the sizeOptionValueIds state from params
  useEffect(() => {
    if (!isEqual(updatedSizeOptionValueIds, currentSizeOptionValueIds))
      setCurrentSizeOptionValueIds(updatedSizeOptionValueIds);
  }, [updatedSizeOptionValueIds, currentSizeOptionValueIds, setCurrentSizeOptionValueIds]);

  // to update the sortBy state from the params
  useEffect(() => {
    if (sortBy !== currentSortBy) setCurrentSortBy(sortBy);
  }, [sortBy, currentSortBy, setCurrentSortBy]);

  // to update the decoration method state from the params
  useEffect(() => {
    if (!isEqual(updatedDecorationTypeValues, currentDecorationMethods))
      setCurrentDecorationMethods(updatedDecorationTypeValues);
  }, [updatedDecorationTypeValues, currentDecorationMethods, setCurrentDecorationMethods]);

  const selectedProductionDays = useMemo(
    () => (queryProductionDays ? queryProductionDays.split(",").map(Number) : emptyArr),
    [queryProductionDays],
  );
  const selectedItemFeatures = useMemo(
    () => (queryItemFeatures ? queryItemFeatures.split(",") : emptyArr),
    [queryItemFeatures],
  );

  const selectedItemTypes = useMemo(() => (itemType ? itemType?.split(",") : emptyArr), [itemType]);
  const designedItemTypes = useMemo(
    () => (designedType ? designedType?.split(",") : emptyArr),
    [designedType],
  );

  const [onDemandModalShown, setOnDemandModalShown] = useWindowStorage(
    ONDEMAND_MODAL_SHOWN_KEY,
    !selectedItemTypes.includes(LOCKER_INVENTORY_FILTER_KEY),
    "sessionStorage",
  );

  const setFilterToDefault = () => {
    updateUrl({
      countryIds: emptyArr,
      dietaryRestrictionIds: emptyArr,
      priceRangeIds: emptyArr,
      sizeOptionValueIds: emptyArr,
      colorOptionValueIds: emptyArr,
      brandValueIds: emptyArr,
      decorationTypeValues: emptyArr,
      page: INITIAL_PAGE,
      itemType: "",
      itemFeatures: "",
      productionDays: "",
      custom: "",
      designedType: "",
    });
    setIsPriceRangeFilterEnabled(false);
    setSelectedPriceRangeIds(INIT_PRICE_RANGE_IDS);
  };

  const filterTypes = [
    selectedDietaryLabels,
    selectedBrandLabels,
    selectedPriceRangeLabels,
    selectedCountryIdTypes,
    selectedColorOptionTypes,
    currentDecorationMethods,
    selectedSizeOptionTypes,
    selectedProductionDays,
    custom,
    selectedItemTypes,
    selectedItemFeatures,
    designedItemTypes,
  ];

  const totalFiltersApplied = filterTypes.reduce((total, filter) => {
    if (Array.isArray(filter)) {
      total += [ALL_DESIGNED_PRODUCTS_FILTER.id, ALL_PRODUCTS_FILTER.id].includes(filter[0])
        ? 0
        : filter.length;
    } else {
      total += filter ? 1 : 0;
    }
    return total;
  }, 0);

  const hasFiltersApplied = Boolean(totalFiltersApplied);

  // it is customizable ONLY if any of customizable or designed filter is added. If both are added, then it's the default case - i.e. show all.
  const isCustomizable =
    custom ||
    intersection([CUSTOMIZABLE_FILTER.id, DESIGNED_FILTER.id], selectedItemFeatures).length === 1;

  const data = useMemo(
    () => ({
      currentDietaryRestrictionsIds,
      currentPage,
      currentPriceRangeIds,
      selectedPriceRangeIds,
      currentCountryIds,
      currentColorOptionValueIds,
      currentSizeOptionValueIds,
      currentSortBy,
      priceRanges,
      colorOptionValues,
      countryIdValues,
      sizeOptionValues,
      requiredDietaryRestrictionIds,
      selectedDietaryLabels,
      selectedPriceRangeLabels,
      selectedCountryIdTypes,
      selectedColorOptionTypes,
      selectedSizeOptionTypes,
      setDietaryRestrictionIds,
      setCountryIds,
      setDietaryRestrictions,
      setPage,
      setPriceRangeIds,
      setPriceRanges,
      setRequiredDietaryRestrictionIds,
      setColorOptionValues,
      setColorOptionValueIds,
      setSizeOptionValues,
      setSizeOptionValueIds,
      setSortBy,
      boxProductList,
      handleBoxProductList,
      handleRoutePathForFilters,
      isPriceRangeFilterEnabled,
      setIsPriceRangeFilterEnabled,
      totalProducts,
      setTotalProducts,
      customizable: isCustomizable,
      setCustomizableFilter,
      isFetchingProducts,
      setIsFetchingProducts,
      setFilterToDefault,
      brandValues,
      setBrandValues,
      currentBrandIds,
      flattenedBrandValueIds,
      setBrandValueIds,
      selectedBrandLabels,
      itemType,
      setProductionDaysFilter,
      setProductionDays,
      productionDays,
      selectedProductionDays,
      bulkProductsCount,
      setBulkProductsCount,
      onDemandProductsCount,
      setOnDemandProductsCount,
      coreOnDemandProductsCount,
      setCoreOnDemandProductsCount,
      selectedItemFeatures,
      setItemFeatures,
      itemFeatures,
      setItemFeaturesFilter,
      hasFiltersApplied,
      onDemandModalShown,
      setOnDemandModalShown,
      volumeDiscountProductsCount,
      setVolumeDiscountProductsCount,
      perPage,
      setPerPage: updatePerPage,
      printTypeValues,
      setPrintTypeValues,
      currentDecorationMethods,
      setDecorationMethodsFilter,
      selectedItemTypes,
      setSelectedItemTypes,
      totalFiltersApplied,
      designedItemTypes,
      setSelectedDesignedTypes,
    }),
    [
      currentDietaryRestrictionsIds,
      currentPage,
      currentPriceRangeIds,
      selectedPriceRangeIds,
      currentColorOptionValueIds,
      currentCountryIds,
      currentSizeOptionValueIds,
      currentSortBy,
      priceRanges,
      colorOptionValues,
      countryIdValues,
      sizeOptionValues,
      requiredDietaryRestrictionIds,
      selectedDietaryLabels,
      selectedPriceRangeLabels,
      selectedCountryIdTypes,
      selectedColorOptionTypes,
      currentDecorationMethods,
      selectedSizeOptionTypes,
      setDietaryRestrictionIds,
      setCountryIds,
      setDietaryRestrictions,
      setPage,
      setPriceRangeIds,
      setPriceRanges,
      setRequiredDietaryRestrictionIds,
      setColorOptionValues,
      setColorOptionValueIds,
      setSizeOptionValues,
      setSizeOptionValueIds,
      setSortBy,
      boxProductList,
      isPriceRangeFilterEnabled,
      setIsPriceRangeFilterEnabled,
      totalProducts,
      setTotalProducts,
      custom,
      setCustomizableFilter,
      isLockerShop,
      isFetchingProducts,
      setIsFetchingProducts,
      setFilterToDefault,
      brandValues,
      setBrandValues,
      currentBrandIds,
      flattenedBrandValueIds,
      setBrandValueIds,
      selectedBrandLabels,
      itemType,
      productionDays,
      setProductionDays,
      selectedProductionDays,
      bulkProductsCount,
      setBulkProductsCount,
      onDemandProductsCount,
      setOnDemandProductsCount,
      coreOnDemandProductsCount,
      setCoreOnDemandProductsCount,
      setItemFeaturesFilter,
      onDemandModalShown,
      volumeDiscountProductsCount,
      setVolumeDiscountProductsCount,
      perPage,
      updatePerPage,
      printTypeValues,
      setPrintTypeValues,
      currentDecorationMethods,
      setDecorationMethodsFilter,
      setSelectedItemTypes,
      selectedItemTypes,
      designedItemTypes,
      setSelectedDesignedTypes,
    ],
  );
  return <ProductListingContext.Provider value={data}>{children}</ProductListingContext.Provider>;
};

const NewProductListingProvider = ({ children }) => {
  const [boxProductList, setBoxProductList] = useState([]);

  const value = useMemo(
    () => ({
      boxProductList,
      setBoxProductList,
      handleBoxProductList: setBoxProductList,
    }),
    [boxProductList],
  );

  return <ProductListingContext.Provider value={value}>{children}</ProductListingContext.Provider>;
};

const ProductListingProvider = ({ children }) => {
  const { isNewSwagmagicHost } = useStoreContext();

  if (isNewSwagmagicHost) {
    return <NewProductListingProvider>{children}</NewProductListingProvider>;
  }

  return <OldProductListingProvider>{children}</OldProductListingProvider>;
};

export default ProductListingProvider;
